// Whether a row offers Create Quote, and where it goes. Pure over OpportunityNode (no React, no design
// system), so every gate combination is unit-tested without mounting anything — same isolation as
// opportunityInlineEdit.ts. Spec 010 D13–D15.
import util from '../shared/util/util';
import {isQuotesEnabled} from '../shared/util/FeatureUtil';
import {hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import {projectUrl} from '../../directApi';
import type {OpportunityNode} from './opportunityTypes';

export type CreateQuoteTarget = {show: false} | {show: true; href: string; destination: 'Quote Designer' | 'Baseline'};

const hasDates = (n: OpportunityNode): boolean =>
	!!(
		n.projectStartYear &&
		n.projectStartMonth &&
		n.projectStartDay &&
		n.projectEndYear &&
		n.projectEndMonth &&
		n.projectEndDay
	);

export const createQuoteTarget = (node: OpportunityNode): CreateQuoteTarget => {
	// D13: only "No quote". The Baseline page also offers Create for REVISING; the PRD does not.
	if (node.quote) return {show: false};
	// D14: the quote resolver returns null both when quotes are off and when the viewer lacks full access,
	// so "no quote" can mean "unknown" — offer Create only when the viewer could actually use it.
	if (!isQuotesEnabled()) return {show: false};
	if (!hasPermission(PERMISSION_TYPE.QUOTE_CREATE)) return {show: false};
	// The server's write rule (QuoteLogic.createQuote -> verifyPersonOnProject): on the team, or Access all projects.
	// fullAccessToProject is not enough: a salesperson who isn't on the team has it, and their save would be rejected.
	if (node.readOnlyAccess || node.writeAccessToProject !== true) return {show: false};
	// Both destinations redirect away unless this is a Baseline project the viewer can open: the Baseline page
	// sends anything else to the task board, the designer back to the Baseline. isBaselineProject also covers the
	// 'project-initial-plan' route guard (via hasBaselinePageAccess), the BASELINE module, the RETAINER flag and
	// baselineRouteAccess.
	if (!util.isBaselineProject(null, node)) return {show: false};

	const base = projectUrl(node.companyProjectId, node.customProjectId);
	// D15: only fields the list already loads. The designer's own direct-URL guards handle the rest.
	return hasDates(node)
		? {show: true, href: `${base}/baseline/quote`, destination: 'Quote Designer'}
		: {show: true, href: `${base}/baseline`, destination: 'Baseline'};
};
